import React, { useContext } from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import parse, { attributesToProps, domToReact } from 'html-react-parser';
import sanitize from 'sanitize-html';
import { patchImageUrl } from '@culture/common-helpers/cultureUtils';

import Script from 'next/script';
import { ImageFormatContext } from 'contexts/ImageFormatContext';
import LazyLoadImage from 'components/LazyLoad/Image';

import { getInitialLetterIndex } from 'utils/helpers/string';

import styles from '../styles.module.scss';

export const iframeSanitizeParams = {
	allowVulnerableTags: true,
	allowedTags: sanitize.defaults.allowedTags.concat([
		'iframe',
		'img',
		'script',
		'center',
		'i',
		'b'
	]),
	allowedAttributes: {
		...sanitize.defaults.allowedAttributes,
		img: ['src', 'alt', 'style'],
		p: ['align'],
		div: ['class', 'id', 'style', 'width', 'height'],
		iframe: [
			'src',
			'width',
			'height',
			'title',
			'frameBorder',
			'frameborder',
			'allow',
			'allowfullscreen',
			'style',
			'gesture'
		],
		span: ['class', 'data-hint-text', 'data-hint-img']
	}
};

const StyledContentHtml = ({ html, firstParagraph }) => {
	const imageFormat = useContext(ImageFormatContext);

	let htmlToParse;
	const initialLetterIndex = getInitialLetterIndex(html);
	if (initialLetterIndex !== -1 && firstParagraph) {
		const initialLetter = html.slice(
			initialLetterIndex,
			initialLetterIndex + 1
		);

		const initialLetterTagString = ReactDOMServer.renderToString(
			<span key={initialLetter} className={styles.initialParagraph}>
				{initialLetter}
			</span>
		);

		const resultHtml =
			html.substring(0, initialLetterIndex) +
			initialLetterTagString +
			html.substring(initialLetterIndex + 1);

		htmlToParse = sanitize(resultHtml, iframeSanitizeParams);
	} else {
		htmlToParse = sanitize(html, iframeSanitizeParams);
	}

	return parse(htmlToParse, {
		replace(domNode) {
			const { type, name, attribs, children } = domNode;

			if (type === 'tag' && name === 'img' && attribs.src) {
				const imageSrc = patchImageUrl(attribs.src, { format: imageFormat });
				const props = attributesToProps({ ...attribs, src: imageSrc });

				return (
					<LazyLoadImage imageSrc={imageSrc}>
						<img alt="" {...props} />
					</LazyLoadImage>
				);
			}

			if (type === 'script') {
				const isExternalScript = !!domNode.attribs.src;
				const props = attributesToProps({ ...attribs });

				if (isExternalScript) {
					return <Script {...props} />;
				}
				const scriptChildren = children ? children[0]?.data : '';
				return <Script>{scriptChildren}</Script>;
			}

			if (type === 'tag' && name === 'a') {
				const props = attributesToProps({ ...attribs });

				return (
					<a {...props} className={styles.htmlLink}>
						{domToReact(children)}
					</a>
				);
			}

			return domNode;
		}
	});
};

StyledContentHtml.propTypes = {
	html: PropTypes.string.isRequired,
	firstParagraph: PropTypes.bool
};

StyledContentHtml.defaultProps = {
	firstParagraph: false
};

export default StyledContentHtml;
